import { ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from 'react';
const Hero = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [dots, setDots] = useState<{
    x: number;
    y: number;
    size: number;
    opacity: number;
    speed: number;
  }[]>([]);
  useEffect(() => {
    // Generate static background dots - no animation to improve performance
    const generateDots = () => {
      const newDots = [];
      for (let i = 0; i < 30; i++) { // Reduced from 50 to 30
        newDots.push({
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 3 + 1,
          opacity: Math.random() * 0.2 + 0.05,
          speed: Math.random() * 0.5 + 0.1
        });
      }
      setDots(newDots);
    };
    generateDots();

    // Initialize the Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('show');
          // Once the animation has played, no need to observe this element anymore
          observerRef.current?.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    } // Trigger when at least 10% of the element is visible
    );

    // Select all elements with the scroll-trigger class
    const scrollElements = document.querySelectorAll('.scroll-trigger');
    scrollElements.forEach(el => {
      observerRef.current?.observe(el);
    });

    // Cleanup function
    return () => {
      if (observerRef.current) {
        scrollElements.forEach(el => {
          observerRef.current?.unobserve(el);
        });
      }
    };
  }, []);
  return <section className="relative bg-gradient-primary min-h-[85vh] flex items-center overflow-hidden" id="hero">
      {/* Animated dots background */}
      <div className="absolute inset-0 overflow-hidden">
        {dots.map((dot, index) => <div key={index} className="absolute rounded-full bg-cyanPrimary" style={{
        left: `${dot.x}%`,
        top: `${dot.y}%`,
        width: `${dot.size}px`,
        height: `${dot.size}px`,
        opacity: dot.opacity,
        transition: 'all 0.5s linear'
      }} />)}
      </div>
      
      {/* Background elements - dot pattern for texture */}
      <div className="absolute inset-0 dot-pattern opacity-20"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10 -mt-12">
        <div className="grid grid-cols-1 gap-6 items-center">
          <div className="space-y-4 max-w-4xl mx-auto text-center">
            <div className="flex justify-center">
              <span className="inline-block px-4 py-1.5 bg-darkText/5 text-darkText rounded-full text-sm font-medium scroll-trigger">
                Strategic Product Leadership
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display leading-tight md:leading-[1.2] scroll-trigger text-darkText">
              Turning <span className="text-cyanPrimary font-bold">bold ideas</span> into 
              <br />
              <span className="text-cyanPrimary font-bold">best-in-class</span> digital experiences
            </h1>
            <p className="text-xl md:text-2xl text-darkText/80 scroll-trigger py-[5px]">
              Fractional Digital Product Leadership for Mission-Driven Organizations
            </p>
            <p className="text-lg text-darkText/70 mb-4 scroll-trigger font-light py-[5px]">
              Where vision + execution <span className="font-medium text-darkText">align</span>
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 scroll-trigger">
              <a href="#contact" className="w-full sm:w-auto">
                <Button className="bg-cyanPrimary hover:bg-cyanLight text-white flex items-center justify-center w-full sm:w-auto gap-2 text-lg py-6 px-8 rounded-xl shadow-soft hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
                  Let's Talk <ArrowRight size={18} className="animate-pulse-soft" />
                </Button>
              </a>
              <a href="#services" className="w-full sm:w-auto">
                <Button variant="outline" className="border-grayMedium text-darkText text-lg w-full sm:w-auto py-6 px-8 rounded-xl hover:bg-cyanPrimary/10 transition-all duration-300 transform hover:-translate-y-1">
                  Explore Services
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator - positioned higher */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center">
        <div className="animate-bounce flex flex-col items-center">
          <p className="text-xs text-darkText mb-2 tracking-wider">Scroll to explore</p>
          <div className="w-0.5 h-6 bg-darkText/30 rounded"></div>
        </div>
      </div>
    </section>;
};
export default Hero;